import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { initAirbridge, captureAirbridgeContextFromUrl } from "@/utils/airbridge";
import { installAxiosAuthInterceptor } from "@/utils/axiosAuthInterceptor";
import { hydrateAuthFromNative } from "@/utils/authStorage";
import { captureNativeLaunchContext } from "@/utils/nativeDeepLinks";

// Start reading the native launch context (App Link launch intent + Play
// Store install referrer) IMMEDIATELY — before React mounts — so cold-start
// deep links are buffered before any routing decision can stomp them.
// Instant no-op on plain web/PWA.
captureNativeLaunchContext();

// Force light colour-scheme at runtime as a belt-and-suspenders for older
// Android browsers / Samsung Internet engines wrapped inside a TWA that
// ignore the static <meta name="color-scheme"> tag and only honour the
// inline style. The CSS variables in index.css are already light-only,
// but Chrome's "auto-dark" heuristics can still recolour elements that
// don't have an explicit background — setting colorScheme here turns
// that heuristic off at the runtime level.
if (typeof document !== 'undefined' && document.documentElement) {
  try {
    document.documentElement.style.colorScheme = 'light';
  } catch {
    /* readonly in very old browsers — meta tag remains the fallback */
  }
}

// Initialise the Airbridge Web SDK before React mounts so the first
// `app_open` event and any attribution params on the landing URL are
// captured. Safe no-op if env vars are missing.
initAirbridge();
captureAirbridgeContextFromUrl();

// Global axios 401 handler — when the JWT expires, every API call in
// the app surfaces "Token has expired" and the screen feels broken
// until the user manually logs out. This installs a single interceptor
// at boot that catches expired-token 401s, clears the stale session,
// and redirects to /login with a friendly toast.
installAxiosAuthInterceptor();

// One-time migration (Feb 2026): clear the legacy
// `allure_passkey_prompt_dismissed_at` cooldown so existing users who
// dismissed the prompt early — before it was fully wired up on session
// restore — get one more opportunity to enable biometric unlock on
// their next login. Sentinel key ensures this only runs once per
// browser; future dismissals are respected normally.
try {
  const SENTINEL = 'allure_passkey_prompt_dismissed_reset_v1';
  if (typeof window !== 'undefined' && window.localStorage && !window.localStorage.getItem(SENTINEL)) {
    window.localStorage.removeItem('allure_passkey_prompt_dismissed_at');
    window.localStorage.setItem(SENTINEL, '1');
  }
} catch {
  /* localStorage unavailable (private mode / disabled) — safe no-op */
}

// One-time repair (Feb 2026): the first notification prompt shipped with a
// long dismissal cooldown. For the production Android TWA push incident we
// need every signed-in device to see the corrected prompt/sync flow once more.
try {
  const SENTINEL = 'allure_notifications_prompt_dismissed_reset_v1';
  if (typeof window !== 'undefined' && window.localStorage && !window.localStorage.getItem(SENTINEL)) {
    window.localStorage.removeItem('allure_notifications_prompt_dismissed_at');
    window.localStorage.setItem(SENTINEL, '1');
  }
} catch {
  /* localStorage unavailable (private mode / disabled) — safe no-op */
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// Hydrate the token/user from Capacitor Preferences (native SharedPreferences)
// BEFORE React mounts so App.js sees a "still-signed-in" state on the very
// first render even if the WebView's localStorage was wiped by an APK update.
// hydrateAuthFromNative is idempotent and never throws, so if Preferences
// isn't available (web, private mode) we just render immediately.
//
// SAFETY NET: race against a 3-second timeout so a hung Capacitor bridge
// (can occur when the WebView renderer is restarted by Android) never leaves
// the user on a blank screen forever.
const HYDRATE_TIMEOUT_MS = 3000;
Promise.race([
  hydrateAuthFromNative(),
  new Promise(resolve => setTimeout(resolve, HYDRATE_TIMEOUT_MS)),
]).finally(() => {
  root.render(
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
});
